import { Button, Col, DatePicker, Modal, Popconfirm, Radio, RadioChangeEvent, Row, Select, Spin, Tabs, message } from 'antd';
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import { ReactComponent as CloseIcon } from "../../../../assets/svgs/icons/close-icon.svg";
import { TransactionTableContainer } from './transaction-table.style';
import { getTransactionLog, get_viewable_billing_groups, list_by_billing_group } from './TransactionLambdaUtils';
import { InfoCircleOutlined } from '@ant-design/icons'; 
import dayjs, { Dayjs } from "dayjs";
import TransactionDisputeModal from './TransactionDisputeModal';
import { useNavigate } from 'react-router-dom';

interface TransactionData {
    billing_group: string;
    init_time_str: string;
    data_id: string;
    transaction_id: string;
    user_id: string;
    owner_id: string;
    type: string;
    size_gb: number;
    rate: number | null | "";
    status: string;
    billingStatus: string;
    info: string;
    cost: number | null | "";
    init_time: number;
    expiration_time: number;
}

interface SelectedTransactionData {
    billingGroupId: string;
    transactionId: string;
}
// {selectedBatchItems}: DataIndexItem[]
const TransactionDetails = () => {
    const defaultStartDate = dayjs().startOf("day").subtract(1, 'month');
    const defaultEndDate = dayjs().endOf("day");  
    const disabledDate = (current: Dayjs): boolean => { 
        return current && current > dayjs().startOf("day");
    };
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [transactionData, setTransactionData] = useState<TransactionData[]>([]);
    const [startDate, setStartDate] =  useState<Dayjs | null>(defaultStartDate);
    const [endDate, setEndDate] = useState<Dayjs | null>(defaultEndDate);
    const [selectedType, setSelectedType] = useState(1);
    const [selectedTimeDate, setSelectedTimeDate] = useState(1);
    const [billingGroupData, setBillingGroupData] = useState<any[]>([]);
    const [showBillingGroup, setShowBillingGroup] = useState<boolean>(false);
    const [selectedBillingGroup, setSelectedBillingGroup] = useState<string | null>('');
    const [billingGroupLoading, setBillingGroupLoading] = useState<boolean>(false);
    const [disputeNoteModal, setDisputeNoteModal] = useState<boolean>(false);
    const [applyButtonLoading, setApplyButtonLoading] = useState<boolean>(false);
    const [typeOptions, setTypeOptions] = useState<string[]>([]);
    const [show, setShow] = useState<boolean>(true);
    const [selectedTransactionData, setSelectedTransactionData] = useState<SelectedTransactionData>({
        billingGroupId: '',
        transactionId: '',
    });
    const handleCancel = () => {
        setShow(false)
        navigate('/')
    };
    const callLambdaFunction = async () => {
        setLoading(true);
        getTransactionLog(startDate?.unix(), endDate?.unix()).then((res: any) => {
            const data = JSON.parse(res); 
            const typeOptionsData: string[] = Array.from(new Set(data.data.map((item: TransactionData) => item.type)));
            setTypeOptions(typeOptionsData);
            setTransactionData(data.data);
        }).catch((err) => {
            message.error("Failed to fetch transaction data");
            console.log(err);
        }).finally(() => {
            setLoading(false);
        });
    };

    const callBillingGroupListLambdaFunction = async () => {
        setLoading(true);
        list_by_billing_group(selectedBillingGroup, startDate?.unix(), endDate?.unix()).then((res: any) => {
            const data = JSON.parse(res); 
            const typeOptionsData: string[] = Array.from(new Set(data.data.map((item: TransactionData) => item.type)));
            setTypeOptions(typeOptionsData);
            setTransactionData(data.data);
        }).catch((err) => {
            message.error("Failed to fetch transaction data");
            console.log(err);
        }).finally(() => {
            setLoading(false);
            setApplyButtonLoading(false)
        });
    };


    useEffect(() => {
        callLambdaFunction();
    }, [])


    const transaction_columns = [
        { title: "Billing Group", key: "billing_group", size: 100 },
        { title: "Init Time", key: "init_time", size: 120, },
        { title: "Type", key: "type", size: 50 },
        { title: "Size", key: "size_gb", size: 120 },
        { title: "Rate", key: "rate", size: 50 },
        { title: "Status", key: "status", size: 50 },
        { title: "Billing Status", key: "bill_status", size: 50 },
        { title: "Information", key: "info", size: 20 },
        { title: "Cost", key: "cost", size: 100 },
    ];


    const columns = useMemo<MRT_ColumnDef<TransactionData>[]>(() => {
        if (transactionData.length === 0) {
            return [];
        } else {
            return transaction_columns.map(({ title, key, size }) => {
                if (key === "init_time") {
                    return {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                        Cell: ({ cell }) => {
                            const timeStamp = cell.getValue<string>();
                            const localTime = dayjs.unix(Number(timeStamp)).format("YYYY-MM-DD HH:mm:ss");
                            return <span>{localTime}</span>;
                        },
                    };
                } else if (key === "info") {
                    return {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                        Cell: ({ row }) => (
                            <Popconfirm
                                placement="leftBottom"
                                title={null}
                                description={
                                    <div className='transaction_info_box'>
                                        <div>
                                            <Row gutter={16}>
                                                <Col className="gutter-row" span={8}>
                                                    <div>Owner ID</div>
                                                </Col>
                                                <Col className="gutter-row_right" span={16}>
                                                    <div>{row.original.owner_id}</div>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col className="gutter-row" span={8}>
                                                    <div>Transaction ID</div>
                                                </Col>
                                                <Col className="gutter-row_right" span={16}>
                                                    <div>{row.original.transaction_id}</div>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col className="gutter-row" span={8}>
                                                    <div>Data ID</div>
                                                </Col>
                                                <Col className="gutter-row_right" span={16}>
                                                    <div>{row.original.data_id}</div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>}
                                okText="Dispute"
                                cancelText={null}
                                style={{ backgroundColor: 'red', color: 'white', cursor: "pointer" }}
                                overlayClassName='transaction_info_wrapper'
                                onConfirm={() => handleDispute(row.original.transaction_id,row.original.owner_id)}
                            >
                                <InfoCircleOutlined
                                    style={{ cursor: 'pointer', paddingLeft: "30px" }}
                                />
                            </Popconfirm>
                        ),
                        filterable: false,
                    };
                } else if (key === "rate") {
                    return {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                        Cell: ({ cell }) => {
                            const rateValue = cell.getValue<number | null | "">();
                            return rateValue ? <span>${rateValue.toFixed(4)}</span> : <span>${(0).toFixed(4)}</span>;
                        },
                    };
                }
                else if (key === "cost") {
                    return {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                        Cell: ({ cell }) => {
                            const rateValue = cell.getValue<number | null | "">();
                            return rateValue ? <span>${rateValue.toFixed(4)}</span> : "$0.0000";
                        },
                    };
                } else if (key === "size_gb") {
                    return {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                        Cell: ({ cell }) => {
                            const sizeValue = cell.getValue<number>();
                            if(!sizeValue) return null
                            return <span>{sizeValue.toFixed(2)} GB</span>;
                        },
                    };
                }else if (key === "type") {
                    return {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                        filterVariant: 'select',
                        filterSelectOptions: typeOptions.map(type => ({ label: type, value: type })),
                    };
                }else {
                    let data = {
                        accessorKey: key,
                        header: title || "",
                        size: size,
                    }
                    return data
                }
            })
        }
    }, [transactionData]);

    const table = useMaterialReactTable({
        columns,
        data: transactionData,
        initialState: {
            showGlobalFilter: true,
            pagination: { pageSize: 5, pageIndex: 0 }, // Set default rows per page to 5
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <>
                <div className='datePicker_main_block'>
                    <div className="datePicker_block">
                        <div>
                            <span className='date_label_text'>Start Date:{" "}</span>
                            <DatePicker
                                defaultValue={defaultStartDate}
                                value={startDate}
                                disabledDate={disabledDate}
                                className="datePicker_input"
                                onChange={(date, dateString) =>
                                    onDateChange(date as Dayjs, dateString as string, "start")
                                }
                            />
                        </div>
                        <div>
                            <span className='date_label_text'>End Date:{" "}</span>
                            <DatePicker
                                defaultValue={defaultEndDate}
                                value={endDate}
                                disabledDate={disabledDate}
                                className="datePicker_input"
                                onChange={(date, dateString) =>
                                    onDateChange(date as Dayjs, dateString as string, "end")
                                }
                            />
                        </div>
                    </div>
                    <div className='filter_block'>
                        <Radio.Group onChange={onTimeDateChange} value={selectedTimeDate}>
                            <Radio value={1}>Last 1 Month</Radio>
                            <Radio value={6}>Last 6 Month</Radio>
                            <Radio value={0}>Today</Radio>
                        </Radio.Group>
                        <div className='filter_btn'>
                            <Button className='ok_batch_button apply_btn' loading={applyButtonLoading} onClick={applyFilter}>Apply</Button>
                        </div>
                    </div>
                </div>

            </>
        ),
        renderBottomToolbarCustomActions: ({ table }) => (
            <div className="transaction_total_row">
                <span className='transaction_total'><strong>Total = </strong>${totalCost.toFixed(4)}</span>
            </div>
        ),

    });

    if (!table) {
        return null;
    }

    const onDateChange = (date: Dayjs | null, dateString: string, pickerType: 'start' | 'end') => {
        if (pickerType === "start") {
            !date? setStartDate(dayjs("0001-01-01").startOf("day")): setStartDate(date.startOf("day"));
        } else {
            !date ? setEndDate(dayjs().endOf("day")) : setEndDate(date.endOf("day"));
        }
    };

    const applyFilter = () => {
        if (selectedType === 2) {
            callBillingGroupListLambdaFunction()
        } else {
            callLambdaFunction()
        }
    }

    const onTimeDateChange = (e: RadioChangeEvent) => {
        setSelectedTimeDate(e.target.value);
    } 

    useEffect(() => {
        if (selectedTimeDate === 1) {
            setStartDate(dayjs().subtract(1, 'month').startOf('day'));
            setEndDate(dayjs().endOf('day'));
        }
        else if (selectedTimeDate === 6) {
            setStartDate(dayjs().subtract(6, 'month').startOf('day'));
            setEndDate(dayjs().endOf('day'));
        } else{
            setStartDate(dayjs().startOf('day'));
            setEndDate(dayjs().endOf('day'));
        }
    }, [selectedTimeDate]);

    const onTypeChange = (e: RadioChangeEvent) => {
        setSelectedType(e.target.value);
    };

    useEffect(() => {  
        if (selectedType === 2) {
            setBillingGroupLoading(true)
            setShowBillingGroup(true)
            if (billingGroupData.length === 0) {
                getBillingGroup()
            } else {
                setSelectedBillingGroup(billingGroupData[0])
                setBillingGroupLoading(false)
            }
        } else {
            // setTransactionData(initialTransactionData)
            setShowBillingGroup(false)
            setBillingGroupLoading(false)
            applyFilter()
        }
    }, [selectedType]); 

    useEffect(() => {
        applyFilter()
    }, [selectedBillingGroup]); 

    const getBillingGroup = async () => {
        get_viewable_billing_groups().then((res: any) => {   
            setBillingGroupData(res)
            setSelectedBillingGroup(res[0])
        }).catch((err) => {
            message.error("Failed to fetch billing group data");
            console.log(err);
        }).finally(() => {
            setBillingGroupLoading(false);
        });
    }

    const handleGroupChange = (value: string) => {
        setSelectedBillingGroup(value);
    };

    const totalCost = useMemo(() => {
        return transactionData.reduce((acc, item) => acc + (item.cost || 0), 0);
    }, [transactionData]);

    const handleDispute = (transactionId: string, billingGroupId: string) => {
        setSelectedTransactionData({
            "billingGroupId": billingGroupId,
            "transactionId": transactionId
        })
        setDisputeNoteModal(true)
    };

    const tabItems = [
        {
            label: (
                <span>
                    Transaction Log
                </span>
            ),
            key: '1',
            children: (
                <>
                    <div className="inputData_table_searchable dataIndex_table batch_info transaction_table">
                        <div className='radio_box_block'>
                            <label className='radio_label'>Type</label>
                            <Radio.Group onChange={onTypeChange} value={selectedType}>
                                <Radio value={1}>User</Radio>
                                <Radio value={2}>Billing Group</Radio>
                            </Radio.Group>
                            <div className='transaction_select_block'>
                                {showBillingGroup && (
                                    billingGroupLoading ? (
                                        <Spin size='small' className='spin_loader' />
                                    ) : (
                                        billingGroupData && billingGroupData.length > 0 && (
                                            <Select
                                                showSearch
                                                placeholder="Select a group"
                                                style={{ width: 300 }}
                                                optionFilterProp="children"
                                                className='permission_select'
                                                onChange={handleGroupChange}
                                                value={selectedBillingGroup}
                                            >
                                                {billingGroupData.map((option) => (
                                                    <Select.Option key={option} value={option}>
                                                        {option}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        )
                                    )
                                )}
                            </div>

                        </div>
                        <div className='loading_box'>
                            <Spin spinning={loading}>
                                <div className={`materialReactTableCustom batchCustomTable transaction_table`}>
                                    <MaterialReactTable table={table} />
                                </div>
                            </Spin>
                        </div>
                    </div>
                </>

            ),
        },
    ];

    const handleExport = () => { 
         const arrayToCSV = (arr: any) => { 
            const csvRows = [];
     
            const headers = Object.keys(arr[0]);
            csvRows.push(headers.join(','));
     
            for (const row of arr) {
                const values = headers.map(header => {
                    const escape = ('' + row[header]).replace(/"/g, '\\"');
                    return `${escape}`;
                });
                csvRows.push(values.join(','));
            }
    
            return csvRows.join('\n');
        };
    
        const downloadCSV = (csv: string, filename: string) => {
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.setAttribute('hidden', '');
            a.setAttribute('href', url);
            a.setAttribute('download', filename);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        };
    
        const csv = arrayToCSV(transactionData);
        downloadCSV(csv, 'transactionData.csv');
    };

    return (
        <Modal
            centered
            open={show}
            onCancel={handleCancel}
            className="batch_modal create_batch_modal transaction_modal transaction_detail_modal"
            closeIcon={<CloseIcon key="CloseIcon" color='#ffffff' onClick={handleCancel} />}
            footer={[
                <div key="footer-buttons" className="left-footer-buttons">
                    <Button onClick={handleExport} loading={buttonLoading} type="primary" className="ok_batch_button">
                        Export
                    </Button>
                </div>
            ]}
            style={{
                backgroundColor: "#36BDBE",
                width: "100%", // Adjust the width as per your need
                maxWidth: "100%", // Set maximum width if needed
            }}
        >
            <TransactionTableContainer>
                <div className='table-container'>
                    <div className="custom-tab-container">
                        <Tabs defaultActiveKey="1"
                            items={tabItems}
                            key="1"
                            className="custom-tabs">
                        </Tabs>
                    </div>
                </div>

                {disputeNoteModal && <TransactionDisputeModal setShow={setDisputeNoteModal} selectedTransactionData={selectedTransactionData}/>}
            </TransactionTableContainer>
        </Modal>
    );
}

export default TransactionDetails;

let allPatterns = [
    {
        imageURL: "https://s3.amazonaws.com/assetz-dev/assetz-logo1.png",
        title: "Lion King",
        tags: ["Animal", "Cartoon", "wildlife"]
    },
    {
        imageURL: "https://s3.amazonaws.com/assetz-dev/assetz-logo2.png",
        title: "Monkey",
        tags: ["Animal", "Cartoon"]
    },
    {
        imageURL: "https://s3.amazonaws.com/assetz-dev/assetz-logo3.png",
        title: "Airplane",
        tags: ["Vehicle"]
    },
    {
        imageURL: "https://s3.amazonaws.com/assetz-dev/assetz-logo4.png",
        title: "Camp",
        tags: ["Nature", "Stay", "Adventure"]
    },
]